/**
 * StatusPill — UX §3.10. One pill per place status:
 *   go-to    hairline accent, label accent, transparent fill
 *   avoid    hairline error-muted, label error-muted
 *   untried  hairline ink-300, label ink-500, italic
 *   neutral  hairline ink-200, label ink-600
 *   archived dashed hairline ink-200, label ink-400, italic
 *
 * Never bold, never filled with a bright color. uppercase tracking-widest.
 */
import * as React from 'react';
import type { PlaceStatus } from '@shared/types';
import { cn } from '../../lib/cn';

export interface StatusPillProps extends React.HTMLAttributes<HTMLSpanElement> {
  status: PlaceStatus;
  /** When true, the pill becomes a button role with aria-pressed. */
  interactive?: boolean;
  pressed?: boolean;
}

const BASE = cn(
  'inline-flex items-center gap-1.5',
  'px-2.5 py-0.5',
  'font-sans text-[0.6875rem] uppercase tracking-[0.14em]',
  'border rounded-full',
  'leading-tight',
);

const STATUS_LABEL: Record<PlaceStatus, string> = {
  'go-to': 'Go-to',
  avoid: 'Avoid',
  untried: 'Untried',
  neutral: 'Neutral',
  archived: 'Archived',
};

const STATUS_CLASS: Record<PlaceStatus, string> = {
  'go-to': 'text-accent border-accent bg-transparent',
  avoid: 'text-error-muted border-error-muted bg-transparent',
  untried: 'text-ink-500 border-ink-300 italic bg-transparent',
  neutral: 'text-ink-600 border-ink-200 bg-transparent',
  archived: 'text-ink-400 border-ink-200 border-dashed italic bg-transparent',
};

export const StatusPill = React.forwardRef<HTMLSpanElement, StatusPillProps>(
  function StatusPill(
    { status, interactive = false, pressed, className, children, ...rest },
    ref,
  ) {
    const label = children ?? STATUS_LABEL[status];
    const isButton = interactive;

    if (isButton) {
      return (
        <button
          type="button"
          aria-pressed={pressed ?? false}
          aria-label={`Status: ${STATUS_LABEL[status]}`}
          className={cn(
            BASE,
            STATUS_CLASS[status],
            'transition-colors duration-200 ease-out-slow',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
            className,
          )}
        >
          {label}
        </button>
      );
    }

    return (
      <span
        ref={ref}
        aria-label={`Status: ${STATUS_LABEL[status]}`}
        className={cn(BASE, STATUS_CLASS[status], className)}
        {...rest}
      >
        {label}
      </span>
    );
  },
);
